<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      sort-by="name"
      :search="search"
      class="elevation-0 general-table"
      @click:row="itemEdit"
      :loading="isLoading"
      :loading-text="$t('GLOBAL-LOADING-PLEASE_WAIT')"
    >
      <template v-slot:header.image="{ header }">
        <lang t="PRODUCTION_SYSTEMS-TBL-HEADER-IMAGE"></lang>
      </template>

      <template v-slot:header.name="{ header }">
        <lang t="PRODUCTION_SYSTEMS-TBL-HEADER-NAME"></lang>
      </template>

      <template v-slot:header.type="{ header }">
        <lang t="PRODUCTION_SYSTEMS-TBL-HEADER-TYPE"></lang>
      </template>

      <template v-slot:footer.page-text="props" v-if="items.length > 0">
        <display-number :number="props.pageStart" />-<display-number :number="props.pageStop" /> {{ $t("of") }}
        <display-number :number="items.length" />
      </template>

      <template v-slot:top>
        <div class="d-flex align-center flex-wrap py-2 mb-3">
          <v-text-field
            clearable
            class="general-table__search"
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            :label="$t('GLOBAL-DO_SEARCH')"
            solo
            flat
            dense
            hide-details
          ></v-text-field>

          <v-spacer></v-spacer>

        </div>
      </template>

      <template v-slot:item.image="{ item }">
        <v-img width="32px" height="32px" contain :src="$config.cdn + item.image">
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular size="16" width="2" indeterminate color="grey lighten-2"></v-progress-circular>
            </v-row>
          </template>
        </v-img>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon small class="pa-1" color="grey darken-2" @click.stop.prevent="itemDelete(item)">mdi-trash-can-outline</v-icon>
      </template>

      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">Reset</v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog.show" width="auto" :fullscreen="$vuetify.breakpoint.xsOnly" :persistent="false">
      <v-card>
        <v-card-title>
          <span class="title1"><lang :t="dialog.title"></lang></span>
        </v-card-title>

        <v-card-text>
          <div style="max-width: 640px" class="mb-4">
            <v-text-field :label="$t('PRODUCTION_SYSTEMS-LABEL-NAME')" v-model="editedItem.name"></v-text-field>

            <v-row>
              <v-col cols="6" v-for="(value, key) in editedItem.settings" :key="key" v-if="key != 'teachin'">
                <v-text-field :label="$t('PRODUCTION_SYSTEMS-LABEL-' + key.toUpperCase())" :data-key="`${key}`" v-model="editedItem.settings[key]"></v-text-field>
              </v-col>
            </v-row>
          </div>

        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeModal" :disabled="dialog.isSaving"><lang t="GLOBAL-BTN-CANCEL"></lang></v-btn>
          <v-btn elevation="0" class="general-action-btn" :loading="dialog.isSaving" @click="itemSave()"><lang t="GLOBAL-BTN-SAVE"></lang></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DisplayNumber from "../../../components/DisplayNumber.vue";
import { converterMixin } from "../../../mixins/converterMixin";
import {cloneDeep} from 'lodash';
export default {
  name: "ProductionSystemsIndex",
  components: {
    DisplayNumber,
  },
  mixins:[converterMixin],
  data: () => ({
    isSaving: false,
    isLoading: true,
    isUploading: false,

    search: "",

    headers: [
      { text: "Name", align: "start", value: "name", sortable: true, class: "general-table__th--2" },
      { text: "Type", value: "type", sortable: true, class: "general-table__th--2" },
      { text: "", value: "actions", sortable: false, width: "80px", align: "end" },
    ],

    items: [],
    defaultItems: [],
    defaultMetricItems: [],
    keySettingsInMilimeters: [
      'maxHeight', 'minHeight', 'maxWidth', 'maxLength', 'maxDimensions', 'minDimensions',
      'minWidth','minLength'
    ],
    keySettingsInGrams: [
      'lowerWeightLimit', 'upperWeightLimit', 'lowerWeightLimitHard', 
      'lowerWeightLimitSoft', 'lowerWeightLimitTray', 'upperWeightLimitHard',
      'upperWeightLimitSoft', 'upperWeightLimitTray', 'lowerWeightLimitOpenBox',
      'upperWeightLimitOpenBox', 'lowDensityWeightThreshold', 'maxWeightUnit', 'maxWeight',
      'minWeight'
    ],
    keySettingsInMM2: [
      'gapsAreaLimit'
    ],
    keySettingsInKgM3: [
      'densityThreshold'
    ],
    dialog: {
      show: false,
      title: "",
      isSaving: false,
    },

    editedIndex: -1,

    editedItem: {
      name: null,
      type: null,
      settings: {},
    },

    defaultItem: {
      name: null,
      type: null,
      settings: {},
    },

    teachInSteps: null,
  }),

  watch: {
    "dialog.show"(val) {
      val || this.closeModal();
    },
  },

  computed: {
  },

  created() {
    this.initialize();
  },

  mounted() {},

  methods: {
    initialize() {
      this.isLoading = true;

      this.axios.get("productionsystems").then((res) => {

        let items = res.data;
        if(items){
          this.defaultMetricItems = cloneDeep(res.data);
          items = items.map((item) => {
            if(!this.isMetricUnitSystem && item.settings && Object.keys(item.settings).length > 0){
              for(const [key, value] of Object.entries(item.settings)){
                if(this.keySettingsInMilimeters.includes(key)){
                  item.settings[key] = this.milimetersToInch(value);
                }else if(this.keySettingsInGrams.includes(key)){
                  item.settings[key] = this.gramsToLb(value);
                }else if (this.keySettingsInMM2.includes(key)){
                  item.settings[key] = this.mm2ToIn2(value); 
                }else if(this.keySettingsInKgM3.includes(key)){
                  item.settings[key] = this.kgM3TolbInch3(value);
                }

              }

            }

            return item;          
          });
        }
        this.items = cloneDeep(items);
        this.defaultItems = cloneDeep(items);
        this.isLoading = false;
      });
    },

    itemDelete(item) {
      this.$confirm(this.$t("GLOBAL-CONFIRMATION-DELETE-MSG", { NAME: item.name }), {
        title: this.$t("GLOBAL-CONFIRMATION-DELETE-TITLE"),
        buttonTrueText: this.$t("GLOBAL-CONFIRMATION-DELETE-YES"),
        buttonFalseText: this.$t("GLOBAL-CONFIRMATION-DELETE-CANCEL"),
      }).then((res) => {
        if (res) {
          this.axios.delete("productionsystems/" + item.id).then((res) => {
            this.initialize();
            this.$store.dispatch("setSnackbar", { text: this.$t("GLOBAL-SUCCESSFULLY_DELETED") });
          });
        }
      });
    },

    itemEdit(item) {
      this.dialog.title = this.$t("PRODUCTION_SYSTEMS-EDIT", { item: item.name });
      this.editedIndex = this.items.indexOf(item);

      if (typeof item.settings.length != "undefined") {
        item.settings = {};
      }

      if (typeof item.settings === "object" && item.settings !== null) {
        if (typeof item.settings.teachin == "undefined") {
          item.settings.teachin = [];
        }
      }

      this.editedItem = Object.assign({}, item);

      if (this.teachInSteps == null) {
        this.axios.get("teachin/steps").then((res) => {
          this.teachInSteps = res.data;
          this.dialog.show = true;
        });
      } else {
        this.dialog.show = true;
      }
    },

    itemAdd() {
      this.dialog.title = this.$t("PRODUCTION_SYSTEMS-NEW_PRODUCTION_SYSTEM");
      this.dialog.show = true;
    },

    itemSave() {

      this.dialog.isSaving = true;

      let item = cloneDeep(this.editedItem);
      if(!this.isMetricUnitSystem && item.settings && Object.keys(item.settings).length > 0){
        for(const [key, value] of Object.entries(item.settings)){
          const defaultItemIndex = this.defaultItems.findIndex((el) => el.id === item.id);
          let defaultItem = undefined;
          if(defaultItemIndex !== -1){
            defaultItem = this.defaultItems[defaultItemIndex];
          } 
          const changedValue = !defaultItem || defaultItem.settings[key] === undefined || (defaultItem.settings[key] !== undefined && defaultItem.settings[key] !== value);
          if(!changedValue){
            const foundIndex = this.defaultMetricItems.findIndex(el => el.id === item.id);
            if(foundIndex === -1) throw new Error('Cannot find item in default metric list, item with id: '+item.id);
            item.settings[key] = this.defaultMetricItems[foundIndex].settings[key];
            continue;
          }
          if(this.keySettingsInMilimeters.includes(key)){
            item.settings[key] = this.inchToMilimeters(value);
          }else if(this.keySettingsInGrams.includes(key)){
            item.settings[key] = this.lbToGrams(value);
          }else if (this.keySettingsInMM2.includes(key)){
            item.settings[key] = this.in2ToMm2(value); 
          }else if(this.keySettingsInKgM3.includes(key)){
            item.settings[key] = this.lbInch3ToKgM3(value);
          }
        }
      }

      if (this.editedIndex == -1) {
        this.axios
          .post("productionsystems", item)
          .then((res) => {
            this._fin();
          })
          .catch((err) => {
            this.dialog.isSaving = false;
            this.$store.dispatch("setSnackbar", { text: this.$t("GLOBAL-MESSAGE-ERROR") + ": " + err.response.data.message, type: "error" });
          });
      } else {
        this.axios
          .put("productionsystems/" + this.editedItem.id, item)
          .then((res) => {
            this._fin();
          })
          .catch((err) => {
            this.dialog.isSaving = false;
            this.$store.dispatch("setSnackbar", { text: this.$t("GLOBAL-MESSAGE-ERROR") + ": " + err.response.data.message, type: "error" });
          });
      }
    },

    _fin() {
      this.dialog.isSaving = false;
      this.$store.dispatch("setSnackbar", { text: this.$t("GLOBAL-MESSAGE-SUCCESSFULLY_SAVED") });
      this.initialize();
      this.closeModal();
    },

    closeModal() {
      this.dialog.show = false;

      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    getTeachInImgUrl(name) {
      return this.$config.teachInURL + name + ".png";
    },

    selectStep(key, step) {
      if (typeof this.editedItem.settings.teachin == "undefined") {
        this.editedItem.settings.teachin = [];
        this.editedItem.settings.teachin.push(step);
      } else {
        let foundIndex = this.editedItem.settings.teachin.indexOf(step);

        if (foundIndex > -1) {
          this.editedItem.settings.teachin.splice(foundIndex, 1);
        } else {
          this.editedItem.settings.teachin.push(step);
        }
      }

      this.$forceUpdate();
    },

    isSelected(step) {
      if (typeof this.editedItem.settings.teachin == "undefined") {
        return false;
      }

      let found = this.editedItem.settings.teachin.indexOf(step);

      return found > -1 ? true : false;
    },
  },
};
</script>

<style lang="scss">
@import "~vuetify/src/styles/styles.sass";

.teachInGroup {
  .teachInGroupTitle {
    font-size: 18px;
  }

  .teachInStep {
    min-height: 185px;
    position: relative;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.3s ease;

    &:hover {
      background-color: #f0f0f0 !important;
      border: 1px solid #aaa !important;
    }

    .teachInStepTitle {
      display: block;
      color: #494949;
      position: relative;
      z-index: 10000;
    }

    .overlay {
      position: absolute;
      display: none;
      justify-content: center;
      align-items: center;
      z-index: 999;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.4);
      border: 1px solid #cdcdcd;

      i {
        font-size: 68px !important;
      }
    }

    &.is-active {
      background-color: #cdcdcd !important;

      .overlay {
        display: flex;
      }
    }
  }
}
</style>
